import React, { useState, Fragment, useEffect, Suspense, useRef } from "react";
import { useParams } from "react-router-dom";
import Template1 from "../../Components/Jobseeker/ResumeTemplates/Template1/Template1";
import Template2 from "../../Components/Jobseeker/ResumeTemplates/Template2/Template2";
import Template3 from "../../Components/Jobseeker/ResumeTemplates/Template3/Template3";
import Template4 from "../../Components/Jobseeker/ResumeTemplates/Template4/Template4";
import Template5 from "../../Components/Jobseeker/ResumeTemplates/Template5/Template5";
import { Container, Row, Col, Button, Card, CardBody } from "reactstrap";
import Select from "react-select";
import { useNavigate } from "react-router-dom";
import Breadcrumbs from "../../Components/Common/Breadcrumb";
import { FiEdit } from "react-icons/fi";
import { FiDownload } from "react-icons/fi";
import { fetchTemplatesRequest } from "../../redux/resumeTemplates/actions";
import { useDispatch, useSelector } from "react-redux";
import { shareSeekerDataRequest } from "../../redux/jobseeker/share_seeker_data/actions";
import Spinner from "../../Components/Common/Spinner";
import {
  base64ToId,
  convertFileToPdf,
  capitalizeFirstLetter,
} from "../../Components/Common/CommonFunction";
import { showToast } from "../../Components/Common/Toast";
const ResumeScaleWrapper = ({ children, selectedTemplate }) => {
  const containerRef = useRef(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const parent = el.parentElement;
    if (!parent) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const width = entry.contentRect.width || parent.getBoundingClientRect().width;
        if (width > 0) {
          setScale(Math.min(width / 791, 1));
        }
      }
    });

    resizeObserver.observe(parent);

    const initialWidth = parent.getBoundingClientRect().width;
    if (initialWidth > 0) {
      setScale(Math.min(initialWidth / 791, 1));
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [selectedTemplate]);

  return (
    <div
      id={`resume-scale-container-${selectedTemplate}`}
      ref={containerRef}
      style={{
        zoom: scale,
        width: "791px",
        margin: "0 auto",
      }}
    >
      {children}
    </div>
  );
};

const TemplatePreviewPublicPage = () => {
  const { hashCode } = useParams();
  const { templateId } = useParams();
  const [selectedTemplate, setSelectedTemplate] = useState(
    base64ToId(templateId)
  );
  const dispatch = useDispatch();
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    dispatch(
      shareSeekerDataRequest({
        hashcode: hashCode,
      })
    );
  }, []);

  const shareSeekerDataReducerData = useSelector(
    (state) => state.shareSeekerDataReducer
  );

  let jobseekerData = [];
  if (
    !shareSeekerDataReducerData.isLoading &&
    shareSeekerDataReducerData.data
  ) {
    const rawData = shareSeekerDataReducerData.data;
    jobseekerData = {
      ...rawData,
      jobseeker_full_name: rawData.jobseeker_full_name
        ? capitalizeFirstLetter(rawData.jobseeker_full_name)
        : "",
    };
  }

  const resumeDataReady =
    !shareSeekerDataReducerData.isLoading && !!shareSeekerDataReducerData.data;

  const handleDownloadClick = async () => {
    if (!selectedTemplate || !resumeDataReady) {
      showToast(
        "warning",
        "Resume is still loading. Please wait and try again."
      );
      return;
    }
    const resumeEl = document.getElementById(`resume${selectedTemplate}`);
    if (!resumeEl) {
      showToast("error", "Resume preview is not ready. Please try again.");
      return;
    }
    setIsDownloading(true);
    try {
      const candidateName =
        (jobseekerData && jobseekerData.jobseeker_full_name) || "Candidate";

      const templateName = `Template${selectedTemplate}`;
      const cssPath = `${process.env.PUBLIC_URL}/ResumeTemplates/${templateName}.css`;

      const cssResponse = await fetch(cssPath);
      const cssContent = await cssResponse.text();
      const mainContent = resumeEl.innerHTML;

      const htmlCode = `
        <html>
          <head>
            <style>${cssContent}</style>
          </head>
          <body>
            <main>${mainContent}</main>
          </body>
        </html>
      `;

      const blob = new Blob([htmlCode], { type: "text/html" });
      const file = new File([blob], "resume.html", { type: "text/html" });

      await convertFileToPdf(file, candidateName);
      showToast("success", "Resume downloaded.");
    } catch (error) {
      console.error("Error generating PDF:", error);
      showToast("error", "Failed to download the resume. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  };

  console.log("jobseekerData", jobseekerData);
  const [TemplateComponent, setTemplateComponent] = useState(null);

  useEffect(() => {
    // Reset the component when `selectedTemplate` changes
    setTemplateComponent(null);

    // Dynamically import the template component based on `selectedTemplate`
    if (selectedTemplate) {
      import(
        `../../Components/Jobseeker/ResumeTemplates/Template${selectedTemplate}/Template${selectedTemplate}`
      )
        .then((module) => setTemplateComponent(() => module.default))
        .catch((error) => {
          console.error("Error loading template:", error);
          setTemplateComponent(() => null);
        });
    }
  }, [selectedTemplate]);

  return (
    <Fragment>
      <Breadcrumbs title={"Preview Resume"} mainTitle={"Preview Resume"} />

      <Container fluid>
        <Row className="justify-content-center mb-4">
          <Col xl="auto" lg="auto" md="auto" className="p-0">
            {/* Row for button */}
            <Row className="mb-3">
              <div className="d-flex justify-content-end p-0 w-100">
                <Button
                  color="primary"
                  onClick={handleDownloadClick}
                  disabled={
                    isDownloading || !resumeDataReady || !selectedTemplate
                  }
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "5px 20px",
                  }}
                >
                  {isDownloading ? "Downloading…" : "Download resume"}
                  <FiDownload
                    style={{
                      marginLeft: "8px",
                      height: "20px",
                      width: "20px",
                    }}
                  />
                </Button>
              </div>
            </Row>

            <Row className="justify-content-center p-0">
              <Col
                className="d-flex justify-content-center rounded"
                xl="auto"
                lg="auto"
                md="auto"
                style={{ width: "821px", maxWidth: "100%", padding: "0 15px" }}
              >
                <div
                  style={{
                    position: "relative",
                    width: "100%",
                    overflow: "hidden",
                  }}
                >
                  {/* {selectedTemplate == "1" && (
                    <Template1 jobseekerData={jobseekerData} />
                  )} */}
                   <Suspense fallback={<div>Loading...</div>}>
                    {TemplateComponent ? (
                      <ResumeScaleWrapper selectedTemplate={selectedTemplate}>
                        <TemplateComponent jobseekerData={jobseekerData} />
                      </ResumeScaleWrapper>
                    ) : (
                      <Spinner color="primary" />
                    )}
                  </Suspense>
                </div>
              </Col>
            </Row>
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

export default TemplatePreviewPublicPage;
